import React from "react";
import {
  Shirt,
  WashingMachine,
  Scissors,
  Bed,
  Crown,
  GalleryVerticalEnd,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    key: "dry-cleaning",
    title: "Dry Cleaning",
    body: "Suits, dress shirts, dresses, and delicate fabrics — expertly cleaned and crisply pressed.",
    icon: Shirt,
    span: "md:col-span-5 lg:col-span-7",
    image:
      "https://images.unsplash.com/photo-1542058186993-286fdce0b580?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    key: "wash-fold",
    title: "Laundry · Wash & Fold",
    body: "Everyday laundry done right — washed, dried, folded by the pound.",
    icon: WashingMachine,
    span: "md:col-span-3 lg:col-span-5",
  },
  {
    key: "alterations",
    title: "Alterations & Tailoring",
    body: "In-house tailoring for the perfect fit — hems, waists, sleeves, zippers.",
    icon: Scissors,
    span: "md:col-span-4 lg:col-span-4",
  },
  {
    key: "comforters",
    title: "Comforters & Bedding",
    body: "Down, duvets, blankets — sanitized, fluffed and refreshed.",
    icon: Bed,
    span: "md:col-span-4 lg:col-span-4",
    image:
      "https://images.pexels.com/photos/7691101/pexels-photo-7691101.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    key: "leather",
    title: "Leather & Suede",
    body: "Specialty care for jackets, skirts, and pants by trusted partners.",
    icon: GalleryVerticalEnd,
    span: "md:col-span-4 lg:col-span-4",
  },
  {
    key: "wedding-gowns",
    title: "Wedding Gowns",
    body: "Heirloom cleaning, preservation and boxing for your most precious garment.",
    icon: Crown,
    span: "md:col-span-8 lg:col-span-12",
    feature: true,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 lg:py-28 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue">
              Our services
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-navy max-w-2xl">
              Care for every fabric in your closet.
            </h2>
          </div>
          <p className="text-slate-600 max-w-md leading-relaxed">
            From everyday shirts to wedding gowns — Oxford Cleaners delivers
            meticulous, locally-owned garment care since day one.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5">
          {services.map((s) => (
            <article
              key={s.key}
              data-testid={`service-card-${s.key}`}
              className={`lift group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 lg:p-8 flex flex-col ${s.span} ${
                s.feature ? "min-h-[240px]" : "min-h-[260px]"
              }`}
            >
              {s.image && (
                <div className="absolute inset-0 -z-0 opacity-30 group-hover:opacity-50 transition-opacity duration-500">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="h-full w-full object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/85 to-white/40" />
                </div>
              )}

              <div className="relative z-10 flex items-start justify-between">
                <div className="h-12 w-12 rounded-2xl bg-brand-lightblue grid place-items-center text-brand-blue">
                  <s.icon className="h-5 w-5" />
                </div>
                <ArrowUpRight className="h-5 w-5 text-slate-300 group-hover:text-brand-blue group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </div>

              <div className="relative z-10 mt-auto pt-10">
                <h3 className="font-display text-xl lg:text-2xl font-semibold text-brand-navy">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm lg:text-base text-slate-600 leading-relaxed max-w-md">
                  {s.body}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
